import React from 'react';
import { Link } from 'react-router-dom';
import CampaignStatusSelect from './CampaignStatusSelect';

// Campaign type definitions with their icons
const CAMPAIGN_TYPES = {
  general: {
    name: 'General Outreach',
    icon: 'mail'
  },
  anniversary: {
    name: 'Anniversary Recognition',
    icon: 'cake'
  },
  newsletter: {
    name: 'Newsletter',
    icon: 'newspaper'
  },
  nurture: {
    name: 'Relationship Nurturing',
    icon: 'heart'
  },
  recruitment: {
    name: 'Talent Recruitment',
    icon: 'briefcase'
  },
  event: {
    name: 'Event Invitation',
    icon: 'calendar'
  },
  gift: {
    name: 'Gift & Recognition',
    icon: 'gift'
  }
};

const CampaignCard = ({ campaign, onDelete, onEdit, onStatusChange }) => {
  // Format date to a more readable format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate progress percentage
  const progressPercentage = campaign.targetCount > 0 
    ? Math.round((campaign.completedCount / campaign.targetCount) * 100) 
    : 0;

  // Determine status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'planned':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get campaign type info
  const campaignType = campaign.campaignType || 'general';
  const typeInfo = CAMPAIGN_TYPES[campaignType] || CAMPAIGN_TYPES.general;
  
  // Render campaign type icon
  const renderTypeIcon = () => {
    switch (typeInfo.icon) {
      case 'mail':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        );
      case 'cake':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z"></path>
          </svg>
        );
      case 'newspaper':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path>
          </svg>
        );
      case 'heart':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
          </svg>
        );
      case 'briefcase':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        );
      case 'calendar':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        );
      case 'gift':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        );
    }
  };

  return (
    <div className="campaign-card bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-2">
              {renderTypeIcon()}
            </div>
            <h3 className="text-xl font-semibold text-gray-800">{campaign.name}</h3>
          </div>
          <CampaignStatusSelect
            status={campaign.status}
            onStatusChange={(newStatus) => onStatusChange(campaign._id, newStatus)}
          />
        </div>
        
        <div className="flex items-center text-xs text-blue-600 mb-2">
          <span className="mr-1">{typeInfo.name}</span>
          {campaign.sendosoEnabled && (campaign.campaignType === 'gift' || campaign.campaignType === 'anniversary') && (
            <div className="flex items-center ml-2 bg-blue-50 px-2 py-0.5 rounded-full">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
              </svg>
              <span>Physical Gifts</span>
              {campaign.sendosoGiftType && (
                <span className="ml-1">({campaign.sendosoGiftType.replace('_', ' ')})</span>
              )}
            </div>
          )}
        </div>
        
        <p className="text-gray-600 mb-4 line-clamp-2 h-12">{campaign.description}</p>
        
        <div className="flex justify-between text-sm text-gray-500 mb-4">
          <div>
            <span className="block">Start: {formatDate(campaign.startDate)}</span>
            <span className="block">End: {formatDate(campaign.endDate)}</span>
          </div>
          <div className="text-right">
            <span className="block">Target: {campaign.targetCount}</span>
          </div>
        </div>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {campaign.tags && campaign.tags.map((tag, index) => (
            <span 
              key={index} 
              className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs"
            >
              {tag}
            </span>
          ))}
        </div>
        
        {/* Action buttons */}
        <div className="flex justify-between mt-4">
          <Link 
            to={`/app/campaigns/${campaign._id}`} 
            className="text-primary hover:text-blue-700 font-medium text-sm"
          >
            View Details
          </Link>
          <div className="flex gap-2">
            <button 
              className="text-gray-500 hover:text-gray-700 text-sm"
              onClick={() => onEdit && onEdit(campaign)}
            >
              Edit
            </button>
            <button 
              className="text-red-500 hover:text-red-700 text-sm"
              onClick={() => onDelete(campaign._id)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCard;
